// external
import React, { ChangeEvent, FC, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import useSnackbarHelper from '../../hooks/snackbar.hook';
import LockIcon from '@mui/icons-material/Lock';
import { CircularProgress } from '@mui/material';

// internal
import '../login/login.page.css';
import '../reset/reset.page.css';
import whiteLogo from '../../assets/logo-white.png';
import useService from '../../hooks/service.hook';
import AuthenticationService from '../../services/authentication.service';
import { isEmailInvalid } from '../../helpers/general.helpers';
import { useFieldErrors } from '../../components/form-validation';

const ForgotPasswordPage: FC = () => {
  const authenticationService = useService(AuthenticationService);
  // state
  const [email, setEmail] = useState<string>('');
  const [success, setSuccess] = useState<boolean>(false);
  const [loading, setloading] = useState<boolean>(false);
  const { fieldErrors, setFieldErrors, clearFieldError } = useFieldErrors<'email'>();

  // hooks
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbarHelper();

  const handleResetPassword = () => {
    const errors: { email?: string } = {};
    if (!email) {
      errors.email = 'Email is required';
    } else if (isEmailInvalid(email)) {
      errors.email = 'Email is invalid';
    }
    if (errors.email) {
      setFieldErrors(errors);
      return;
    }
    setFieldErrors({});
    {
      setloading(true);
      authenticationService
        .resetPassword(email)
        .then(() => {
          setSuccess(true);
          setloading(false);
          showSnackbar('Email successfully sent', 'success');
        })
        .catch(() => {
          setloading(false);
          setSuccess(false);
          showSnackbar(
            'There was an error while trying to send your reset password email. Please try again later.',
            'error'
          );
        });
    }
  };

  const handleEmailChange = (event: ChangeEvent<HTMLInputElement>) => {
    setEmail(event.target.value);
    clearFieldError('email');
  };

  const getResetPasswordContent = () => {
    if (success) {
      return (
        <div className="reset-page-form-content-header" style={{ height: 300 }}>
          <LockIcon style={{ width: 70, height: 70 }} />
          <p className="title">Reset Password Initiated</p>
          <p className="description">
            Please check your inbox. if there is an account associated with this email than an email will be sent with
            instructions on how to reset your password.
          </p>
          <Button
            variant="contained"
            style={{ textTransform: 'none', marginTop: 10 }}
            color="primary"
            onClick={() => navigate('/login')}
          >
            Back to Login
          </Button>
        </div>
      );
    } else {
      return (
        <>
          <div className="reset-page-form-content-header">
            <LockIcon style={{ width: 70, height: 70 }} />
            <p className="title">Forgot your password?</p>
            <p className="description">
              Enter your email and we will send you an email with instructions on how to get back in to your account
            </p>
          </div>
          <TextField
            id="forgot-field-email"
            value={email}
            onChange={handleEmailChange}
            label="Email"
            variant="outlined"
            size="small"
            error={!!fieldErrors.email}
            helperText={fieldErrors.email || ''}
            style={{ marginBottom: 10 }}
          />
          <Button
            variant="contained"
            style={{ textTransform: 'none', marginTop: 10, marginBottom: 10 }}
            onClick={handleResetPassword}
          >
            Reset Password
          </Button>
          <hr />
          <Button
            variant="contained"
            style={{ textTransform: 'none', marginTop: 10 }}
            color="success"
            onClick={() => navigate('/login')}
          >
            Back to Login
          </Button>
        </>
      );
    }
  };

  return (
    <>
      <div className="login-header">
        <img src={whiteLogo} className="login-header-logo" />
      </div>
      <div className="login-body">
        <form className="login-form" style={{ height: 450 }}>
          {loading ? (
            <div className="reset-page-circle-progress-container">
              <CircularProgress style={{ width: 100, height: 100 }} />
            </div>
          ) : (
            getResetPasswordContent()
          )}
        </form>
      </div>
    </>
  );
};

export default ForgotPasswordPage;
